// CDN image-variant utilities extracted from ui.jsx.
// Maps logical sizes to pixel widths for responsive srcset generation.

export const CDN_RES: Record<string, number> = { thumbnail: 320, mobile: 640, tablet: 1024, desktop: 1920 };

// Media library renditions are named /{aspect}/{width}.webp instead of the legacy
// /{thumbnail|mobile|tablet|desktop}.{ext} scheme. Width tiers per aspect, smallest to
// largest, mirror MidtfynsFestival.BackOffice/docs/image-library-plan.md §3's rendition matrix.
const MEDIA_LIBRARY_WIDTHS: Record<string, number[]> = {
  '1x1': [160, 320, 640, 1024],
  '3x2': [640, 960, 1280, 1920],
  '16x9': [960, 1280, 1920, 2560],
  // Wide 5:1 hero banner (matches the ~380px hero band at desktop widths). Kept in sync with
  // MediaLibraryOptions.Aspects in the BackOffice.
  '5x1': [1280, 1920, 2560, 3440],
};

// Numeric width/height ratio per aspect slug, used by cdnAspectUrl to scale the width cap when
// rewriting one aspect to another.
const ASPECT_RATIO_VALUE: Record<string, number> = {
  '1x1': 1,
  '3x2': 3 / 2,
  '16x9': 16 / 9,
  '5x1': 5,
};

// Matches a media library rendition URL, e.g. /3x2/1920.webp. The optional trailing group
// captures the ?v={renditionVersion} cache-buster the BackOffice appends so it can be preserved
// on rewrite (without it the anchored match would fail on every real, versioned URL).
// Groups: 1 = aspect, 2 = width, 3 = query string (leading '?') or undefined.
const MEDIA_LIBRARY_RE = /\/(1x1|3x2|16x9|5x1)\/(\d+)\.webp(\?[^#]*)?$/i;

// CDN_RES's key order (thumbnail, mobile, tablet, desktop) lines up index-for-index with each
// aspect's width tiers, so legacy size names map onto a sensible tier for media library URLs too.
const LEGACY_SIZE_ORDER = Object.keys(CDN_RES);

export function cdnVariant(url: string | undefined, size: string): string | undefined {
  if (!url) return url;
  const media = url.match(MEDIA_LIBRARY_RE);
  const aspect = media?.[1];
  const tiers = aspect ? MEDIA_LIBRARY_WIDTHS[aspect] : undefined;
  if (media && aspect && tiers) {
    const maxWidth = Number(media[2]);
    const query = media[3] ?? '';
    const tierIndex = LEGACY_SIZE_ORDER.indexOf(size);
    const tierWidth = tiers[tierIndex === -1 ? tiers.length - 1 : tierIndex] ?? maxWidth;
    // The published URL embeds the largest rendition that exists (renditions are clamped to the
    // original width — wider ones are never generated and would 404), so never exceed it.
    const width = Math.min(tierWidth, maxWidth);
    return url.replace(MEDIA_LIBRARY_RE, `/${aspect}/${width}.webp${query}`);
  }
  return url.replace(/\/(thumbnail|mobile|tablet|desktop)(\.[a-z]+)$/i, `/${size}$2`);
}

// Height-to-width factor per source aspect, used to estimate the square (1x1) crop side
// from a landscape rendition's embedded width. The 1x1 crop uses the shorter side, which for
// these landscape aspects is always the height.
const ASPECT_MIN_FACTOR: Record<string, number> = { '1x1': 1, '3x2': 2 / 3, '16x9': 9 / 16 };

// Rewrites a media library rendition URL (any aspect) to its 1x1 (square) counterpart, choosing
// the largest square tier that actually exists. Square renditions are clamped to the source's
// shorter side, so requesting a tier wider than that 404s — hence we derive the cap from the
// landscape rendition's embedded width. Non-media (legacy) URLs are returned unchanged.
export function cdnSquareUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  const media = url.match(MEDIA_LIBRARY_RE);
  const aspect = media?.[1];
  if (!media || !aspect) return url;
  const embeddedWidth = Number(media[2]);
  const query = media[3] ?? '';
  const squareSide = embeddedWidth * (ASPECT_MIN_FACTOR[aspect] ?? 1);
  const tiers = MEDIA_LIBRARY_WIDTHS['1x1'] ?? [];
  // Largest square tier that fits within the available square side (never below the smallest tier).
  const maxTier = tiers.filter((w) => w <= squareSide).pop() ?? tiers[0] ?? embeddedWidth;
  return url.replace(MEDIA_LIBRARY_RE, `/1x1/${maxTier}.webp${query}`);
}

// Rewrites a media library rendition URL (any aspect) to its 16x9 counterpart, choosing the
// largest 16x9 tier that actually exists. 16:9 is the widest aspect the library generates, so
// cropping any source to it preserves the full width — the widest 16x9 rendition is the largest
// tier not exceeding the source width (the embedded rendition width, which is clamped to the
// original). When the source is narrower than the smallest 16x9 tier (no 16x9 rendition was
// generated) or the URL is legacy/non-media, the original URL is returned unchanged. Pass the
// result through cdnVariant/cdnSrcSet to size it — they operate on the 16x9 aspect from there.
export function cdnWideUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  const media = url.match(MEDIA_LIBRARY_RE);
  const aspect = media?.[1];
  if (!media || !aspect) return url;
  const embeddedWidth = Number(media[2]);
  const query = media[3] ?? '';
  const tiers = MEDIA_LIBRARY_WIDTHS['16x9'] ?? [];
  const maxTier = tiers.filter((w) => w <= embeddedWidth).pop();
  if (!maxTier) return url;
  return url.replace(MEDIA_LIBRARY_RE, `/16x9/${maxTier}.webp${query}`);
}

// Rewrites a media library rendition URL to a different aspect, choosing the largest tier of the
// target aspect guaranteed to exist. Both aspects are focus-point-cropped from the same original, so
// their generated widths differ: a NARROWER target (e.g. 16x9 -> 3x2) has a smaller crop, so the
// width cap is scaled down by target/source aspect ratio; a WIDER-or-equal target (e.g. 16x9 -> 5x1)
// is never wider than the source, so the cap stays at the embedded width. Undershooting is safe (a
// smaller tier still exists); overshooting would 404. Returns the URL unchanged for legacy/non-media
// URLs, an unknown target, or a source too small for the target.
export function cdnAspectUrl(url: string | undefined, targetAspect: string): string | undefined {
  if (!url) return url;
  const media = url.match(MEDIA_LIBRARY_RE);
  const sourceAspect = media?.[1];
  const tiers = MEDIA_LIBRARY_WIDTHS[targetAspect];
  if (!media || !sourceAspect || !tiers) return url;
  const embeddedWidth = Number(media[2]);
  const query = media[3] ?? '';
  const sourceVal = ASPECT_RATIO_VALUE[sourceAspect] ?? 1;
  const targetVal = ASPECT_RATIO_VALUE[targetAspect] ?? 1;
  const cap = embeddedWidth * Math.min(1, targetVal / sourceVal);
  const maxTier = tiers.filter((w) => w <= cap).pop();
  if (!maxTier) return url;
  return url.replace(MEDIA_LIBRARY_RE, `/${targetAspect}/${maxTier}.webp${query}`);
}

// Rewrites a media library rendition URL to its 5:1 cinematic hero counterpart. Heroes render the
// image in a ~380px-tall band (≈5:1 at desktop widths), so the 5:1 focus-point crop the BackOffice
// generated is what shows under object-cover instead of a center slice. Falls back to the original
// URL when no 5:1 rendition was generated (source narrower than the smallest tier) or the URL is
// legacy. Pass the result through cdnSrcSet to size it on the 5:1 aspect.
export function cdnHeroUrl(url: string | undefined): string | undefined {
  return cdnAspectUrl(url, '5x1');
}

export function cdnSrcSet(url: string | undefined): string | null {
  if (!url) return null;
  const media = url.match(MEDIA_LIBRARY_RE);
  const aspect = media?.[1];
  const tiers = aspect ? MEDIA_LIBRARY_WIDTHS[aspect] : undefined;
  if (media && aspect && tiers) {
    const maxWidth = Number(media[2]);
    const query = media[3] ?? '';
    // Only the tiers that actually exist: those below the largest rendition, plus the largest
    // itself (the width embedded in the URL). Avoids emitting 404ing wider candidates.
    const widths = tiers.filter((w) => w < maxWidth);
    widths.push(maxWidth);
    return widths
      .map((w) => `${url.replace(MEDIA_LIBRARY_RE, `/${aspect}/${w}.webp${query}`)} ${w}w`)
      .join(', ');
  }
  if (!/\/(thumbnail|mobile|tablet|desktop)\.[a-z]+$/i.test(url)) return null;
  return Object.entries(CDN_RES)
    .map(([name, w]) => `${cdnVariant(url, name)} ${w}w`)
    .join(', ');
}
